import { SUPABASE_URL } from '../../../utils/supabase/url';
import type {
  Database,
  Company,
  User,
  Project,
  ContractItem,
  InterimApplication,
  Tender,
  RetentionLedger,
  Task,
  TaskComment,
  Notification,
  ShareToken,
  RateLibrary,
  MainContractor,
  Contact,
  EstimatingTeamMember,
  Supplier,
  TermsTemplate,
  RateLibraryItem,
  RICSBenchmark,
  CommercialHealthMetric,
  Employer,
  ClientEmployerRelationship,
  EntitlementAlert,
  ActiveDispute,
  ClientRequest,
  PendingDocumentUpload,
} from '../../../types/database';
import type { Claim, ClaimCorrespondence, ClaimSummaryMetrics, ClaimFilters } from '../../../types/claims';
import type { Document } from '../../../types/documents';
import type {
  Activity,
  ActivityFilter,
  ActivityStats,
  CreatePhoneCallData,
  CreateMeetingData,
  CreateEmailData,
  CreateNoteData,
  CreateCommentData,
} from '../../../types/activity';
import { ProjectFoldersService } from '../../ProjectFoldersService';
import { projectId } from '../../../utils/supabase/info';
import { geocodeAddress } from '../../../utils/geocoding';
import type { Constructor } from './mixins';

const API_BASE = `${SUPABASE_URL}/functions/v1/make-server-a476273b`;

export const withCompanies = <TBase extends Constructor<any>>(Base: TBase) => class Companies extends Base {
  // COMPANIES
  // ============================================================================
  // TODO: rename to getWorkspaces() / rekey cache by workspace uuid; flip writes to workspaces. Tracked in follow-up.

  public getCompanies(): Company[] {
    return Object.values(this.db.companies);
  }

  public getCompany(id: string): Company | undefined {
    return this.db.companies[id];
  }

  /**
   * Fetch a single project from the backend and update local cache
   */
  public async fetchProject(id: string): Promise<Project | undefined> {
    if (!this.useSupabase) {
      return this.getProject(id);
    }

    try {
      const url = `${API_BASE}/projects/${id}`;
      const token = await this.getAuthHeaderToken();
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const backendData = await response.json();
        
        // Use helper to map backend data to frontend project
        const project = this.mapBackendProjectToFrontend(backendData);

        // Update local cache
        this.db.projects[id] = project;
        this.saveToLocalStorage();
        this.notifyListeners();
        
        return project;
      } else {
        const errorText = await response.text();
        return this.getProject(id);
      }
    } catch (error) {
      return this.getProject(id);
    }
  }

  /**
   * Fetch a single company from the backend and update local cache
   */
  public async fetchCompany(id: string): Promise<Company | undefined> {
    if (!this.useSupabase) {
      return this.getCompany(id);
    }

    try {
      const url = `${API_BASE}/companies/${id}`;
      const token = await this.getAuthHeaderToken();
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const backendData = await response.json();
        
        // Transform single company from backend format to frontend format
        // Parse address if it's a JSON string (for new backend format)
        let address = backendData.address || '';
        let addressLine1 = backendData.addressLine1 || backendData.address_line_1 || '';
        let addressLine2 = backendData.addressLine2 || backendData.address_line_2 || '';
        let town = backendData.town || '';
        let county = backendData.county || '';
        let postcode = backendData.postcode || '';

        if (address && typeof address === 'string' && (address.startsWith('{') || address.startsWith('"'))) {
          try {
            // Handle double-encoded JSON if necessary (e.g. "\"{\\\"line1...\"}\"")
            let parsed = address;
            if (address.startsWith('"')) {
              try {
                parsed = JSON.parse(address);
              } catch (e) { /* ignore */ }
            }
            
            if (typeof parsed === 'string' && parsed.startsWith('{')) {
              const addressObj = JSON.parse(parsed);
              addressLine1 = addressObj.line1 || addressLine1;
              addressLine2 = addressObj.line2 || addressObj.line3 || addressLine2; // Use line3 as part of line2 if needed
              town = addressObj.town || town;
              county = addressObj.county || county;
              postcode = addressObj.postcode || postcode;
              
              // Reconstruct readable string
              address = [
                addressObj.line1, 
                addressObj.line2, 
                addressObj.line3, 
                addressObj.town, 
                addressObj.county, 
                addressObj.postcode
              ].filter(Boolean).join(', ');
            }
          } catch (e) {
            console.warn('Failed to parse address JSON:', e);
            // Keep original address string if parsing fails
          }
        }

        // Spread the backend payload first so any column the API
        // returns survives the round-trip (organisation_type,
        // subscription_period_*, future-added columns etc.). Then
        // layer the normalised / parsed overrides on top. Mirrors the
        // pattern used by transformDataFromBackend's bulk path; the
        // hand-written allowlist that lived here previously silently
        // dropped any column it forgot to list.
        const company: Company = {
          ...(backendData as any),
          // The detail endpoint can omit classification supplied by the bulk
          // directory load. Preserve it so opening a demo does not move it
          // into Production and remove it from the currently filtered list.
          dataClassification: backendData.dataClassification
            ?? backendData.data_classification
            ?? this.getCompany(id)?.dataClassification
            ?? 'production',
          id: backendData.id,
          name: backendData.name,
          email: backendData.email,
          phone: backendData.phone || '',
          address, // parsed/cleaned address string
          addressLine1,
          addressLine2,
          town,
          county,
          postcode,
          website: backendData.website || '',
          companyNumber: backendData.companyNumber || backendData.company_number || '',
          primaryContact: backendData.primaryContact || backendData.primary_contact || null,
          billing_tier: this.mapLegacyBillingTier(
            backendData.billingTier || backendData.billing_tier || 'bronze'
          ),
          billing_frequency: backendData.billingFrequency || backendData.billing_frequency || 'monthly',
          subscription_price: (backendData.subscriptionPrice || backendData.subscription_price)
            ? parseFloat(backendData.subscriptionPrice || backendData.subscription_price)
            : 0,
          status: backendData.status || 'active',
          notes: backendData.notes || '',
          createdAt: backendData.createdAt || backendData.created_at,
          updatedAt: backendData.updatedAt || backendData.updated_at,
          createdBy: backendData.createdBy || backendData.created_by || '',
          xeroContactId: backendData.xeroContactId || backendData.xero_contact_id || null,
          // Phase-2 read cutover: Company is the *legacy* table —
          // every row is mirrored as a Workspace via
          // workspaces.legacy_company_id. The single-fetch endpoint
          // doesn't join, so leave workspaceId undefined here. The
          // bulk-load mapper in core.ts hydrates it from the joined
          // workspaces collection. Consumers that need the id from a
          // single fetch can call DataService.getWorkspaceForCompany().
          workspaceId: backendData.workspaceId ?? backendData.workspace_id ?? undefined,
        };
        
        // Update local cache
        this.db.companies[id] = company;
        this.saveToLocalStorage();
        this.notifyListeners();
        
        return company;
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to fetch company from backend:', response.status, response.statusText, errorText);
        return this.getCompany(id);
      }
    } catch (error) {
      console.error('❌ Error fetching company from backend:', error);
      return this.getCompany(id);
    }
  }

  public createCompany(company: Omit<Company, 'id' | 'createdAt' | 'updatedAt'>): Company {
    const newCompany: Company = {
      ...company,
      id: this.generateId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this.db.companies[newCompany.id] = newCompany;
    this.saveToLocalStorage();
    // Save to Supabase backend (creates client in companies table)
    // Wait for backend save before logging activity to avoid foreign key errors
    this.saveEntityToBackend('companies', newCompany.id, newCompany, true).then(() => {
      // Log system activity for company creation
      const creator = this.getUser(company.createdBy || '');
      
      this.logActivity({
        type: 'company_created',
        category: 'company',
        action: 'New company registered',
        detail: newCompany.name,
        userId: creator?.id || 'system',
        userName: creator?.name || 'System',
        userEmail: creator?.email || 'system@contractgroup.co.uk',
        metadata: {
          companyId: newCompany.id,
          companyName: newCompany.name,
          companyType: newCompany.type,
          companyStatus: newCompany.status,
        },
      });

    }).catch((err: unknown) => {
      console.error('Error saving company to backend:', err);
    });
    
    // Activity will be logged by the promise callback above after company is saved to backend
    
    this.notifyListeners();
    return newCompany;
  }

  /**
   * Async variant of createCompany — awaits the backend POST before returning.
   * Use this when a subsequent operation (e.g. FK-constrained updateClientRequest)
   * depends on the company row already existing in the database.
   */
  public async createCompanyAsync(company: Omit<Company, 'id' | 'createdAt' | 'updatedAt'>): Promise<Company> {
    const newCompany: Company = {
      ...company,
      id: this.generateId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this.db.companies[newCompany.id] = newCompany;
    this.saveToLocalStorage();

    // ── Await backend save so FK-dependent callers can proceed safely ──
    try {
      await this.saveEntityToBackend('companies', newCompany.id, newCompany, true);
      const creator = this.getUser(company.createdBy || '');
      this.logActivity({
        type: 'company_created',
        category: 'company',
        action: 'New company registered',
        detail: newCompany.name,
        userId: creator?.id || 'system',
        userName: creator?.name || 'System',
        userEmail: creator?.email || 'system@contractgroup.co.uk',
        metadata: {
          companyId: newCompany.id,
          companyName: newCompany.name,
          companyType: newCompany.type,
          companyStatus: newCompany.status,
        },
      });
    } catch (err) {
      console.error('Error saving company to backend:', err);
      delete this.db.companies[newCompany.id];
      this.saveToLocalStorage();
      this.notifyListeners();
      throw err;
    }

    this.notifyListeners();
    return newCompany;
  }

  public async updateCompany(id: string, updates: Partial<Company>): Promise<Company | undefined> {
    const company = this.db.companies[id];
    if (!company) {
      console.error('❌ Company not found in local database:', id);
      return undefined;
    }
    
    const updated = {
      ...company,
      ...updates,
      id, // Prevent ID change
      updatedAt: new Date().toISOString(),
    };

    // BACKEND-FIRST: Save to backend FIRST, then update local only if successful
    if (this.useSupabase) {
      try {
        const transformedData = this.transformDataForBackend('companies', updated);
        const token = await this.getAuthHeaderToken();
        
        const response = await fetch(`${API_BASE}/companies/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(transformedData),
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`❌ Backend update failed:`, response.status, errorText);
          throw new Error(`Backend update failed: ${response.status}`);
        }
        
        // Only update local storage after successful backend save
        this.db.companies[id] = updated;
        this.saveToLocalStorage();
        this.notifyListeners();
        
        return updated;
      } catch (error) {
        console.error(`❌ Error updating company on backend:`, error);
        throw error; // Re-throw to let the UI handle the error
      }
    } else {
      // Fallback: If not using Supabase, just update locally
      this.db.companies[id] = updated;
      this.saveToLocalStorage();
      this.notifyListeners();
      return updated;
    }
  }

  private async syncCompanyToBackend(id: string, company: Company): Promise<void> {
    try {
      // Only send fields that exist in the companies table schema
      // Based on the actual database schema confirmed by the user
      const companyData: any = {
        id: company.id,
        name: company.name,
        type: company.type,
        email: company.email,
        status: company.status,
        updatedAt: company.updatedAt,
      };

      // Include optional fields that exist in the database
      if (company.phone) companyData.phone = company.phone;
      if (company.address) companyData.address = company.address;
      if (company.postcode) companyData.postcode = company.postcode;
      if (company.logo) companyData.logo = company.logo;
      if (company.companyNumber) companyData.companyNumber = company.companyNumber;
      if (company.website) companyData.website = company.website;
      // Using billing_tier instead of deprecated subscription_tier, subscription_status, billing_email
      if (company.billing_tier) companyData.billing_tier = company.billing_tier;
      if (company.billing_frequency) companyData.billing_frequency = company.billing_frequency;
      if (company.subscription_price !== undefined) companyData.subscription_price = company.subscription_price;
      if (company.settings) companyData.settings = company.settings;
      
      // Re-added: These fields DO exist in the database
      if (company.primaryContact) companyData.primaryContact = company.primaryContact;
      if (company.notes) companyData.notes = company.notes;
      if (company.createdBy) companyData.createdBy = company.createdBy;
      
      // Xero integration fields
      if ((company as any).xeroContactId) companyData.xeroContactId = (company as any).xeroContactId;
      if (company.xeroIntegration) companyData.xeroIntegration = company.xeroIntegration;
      const token = await this.getAuthHeaderToken();

      const response = await fetch(`${API_BASE}/companies/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(companyData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to sync company to backend:', errorText);
        
        // If it's a schema error, the migration might not have been run properly
        if (errorText.includes('schema cache') || errorText.includes('column')) {
          console.error('⚠️ Database schema error. The migration may not have been applied correctly. Please verify the companies table schema in Supabase.');
        }
      } else {
        console.log('✅ Company synced to backend successfully:', id);
      }
    } catch (error) {
      console.error('Error syncing company to backend:', error);
    }
  }

  public async deleteCompany(id: string): Promise<boolean> {
    if (!this.db.companies[id]) return false;

    // BACKEND-FIRST: Delete from backend FIRST
    if (this.useSupabase) {
      try {
        const token = await this.getAuthHeaderToken();
        const response = await fetch(`${API_BASE}/companies/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`❌ Backend delete failed:`, response.status, errorText);
          throw new Error(`Backend delete failed: ${response.status}`);
        }
        
        // Only delete locally after successful backend delete
        delete this.db.companies[id];
        this.saveToLocalStorage();
        this.notifyListeners();
        return true;
      } catch (error) {
        console.error(`❌ Error deleting company from backend:`, error);
        throw error;
      }
    } else {
      // Fallback: If not using Supabase, just delete locally
      delete this.db.companies[id];
      this.saveToLocalStorage();
      this.notifyListeners();
      return true;
    }
  }

  // ============================================================================

  // INVOICE CACHING (for Xero)
  // ============================================================================

  /**
   * Get cached invoices for a company
   */
  public getCachedInvoices(companyId: string): any[] | null {
    const cache = this.db.invoiceCache[companyId];
    if (!cache) return null;
    
    // Check if cache is stale (older than 5 minutes)
    const cacheAge = Date.now() - new Date(cache.lastFetched).getTime();
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    
    if (cacheAge > CACHE_DURATION) {
      return null; // Cache is stale
    }
    
    return cache.invoices;
  }

  /**
   * Set cached invoices for a company
   * Note: This does NOT trigger notifyListeners() to avoid cascading renders
   * Invoice cache is ephemeral and doesn't require React re-renders
   */
  public setCachedInvoices(companyId: string, invoices: any[]): void {
    this.db.invoiceCache[companyId] = {
      companyId,
      invoices,
      lastFetched: new Date().toISOString(),
    };
    // Persist through the scoped snapshot writer without notifying listeners.
    this.saveToLocalStorage();
  }

  /**
   * Pre-fetch invoices for a company (call this early to populate cache)
   */
  public async prefetchInvoices(companyId: string): Promise<void> {
    try {
      const company = this.getCompany(companyId);
      if (!company) return;

      const xeroContactId = company.xeroContactId;
      if (!xeroContactId) return;
      const token = await this.getAuthHeaderToken();
      let companyQuery = '';

      const companyConnectionResponse = await fetch(
        `${API_BASE}/xero/check-connection?companyId=${companyId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (companyConnectionResponse.ok) {
        const companyConnection = await companyConnectionResponse.json().catch(() => ({ connected: false }));
        if (companyConnection?.connected) {
          companyQuery = `?companyId=${companyId}`;
        }
      }

      // Fetch invoices from Xero
      const response = await fetch(
        `${API_BASE}/xero/contact/${xeroContactId}/invoices${companyQuery}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) return;

      const result = await response.json();
      if (!result.success) return;

      // Cache the invoices
      this.setCachedInvoices(companyId, result.invoices || []);
    } catch (error) {
      console.error('Error prefetching invoices:', error);
      // Silently fail - don't disrupt user experience
    }
  }

  /**
   * Pre-fetch invoices for all companies with Xero contact IDs
   */
  public async prefetchAllInvoices(): Promise<void> {
    const companies = this.getCompanies().filter(c => 
      c.xeroContactId
    );
    
    // Fetch in parallel but don't wait for all to complete
    Promise.all(companies.map(c => this.prefetchInvoices(c.id)));
  }

  // ============================================================================

  // XERO INTEGRATION MANAGEMENT
  // ============================================================================

  /**
   * Update Xero integration settings for a company
   */
  public async updateXeroIntegration(
    companyId: string,
    integration: {
      connected: boolean;
      connectedDate?: string;
      expiresAt?: string;
      lastSync?: string;
      tenantId?: string;
      tenantName?: string;
      autoCreateQuotes?: boolean;
      autoSendInvoices?: boolean;
    }
  ): Promise<Company | undefined> {
    const company = this.db.companies[companyId];
    if (!company) return undefined;

    const updated = await this.updateCompany(companyId, {
      xeroIntegration: integration,
      updatedAt: new Date().toISOString(),
    });

    return updated;
  }

  /**
   * Get Xero integration status for a company
   */
  public getXeroIntegration(companyId: string) {
    const company = this.db.companies[companyId];
    return company?.xeroIntegration;
  }

  /**
   * Get all companies with Xero integrations
   */
  public getCompaniesWithXero(): Company[] {
    return this.getCompanies().filter(c => c.xeroIntegration?.connected);
  }

  /**
   * Get Xero integration statistics
   */
  public getXeroIntegrationStats() {
    const companies = this.getCompanies();
    const clientCompanies = companies.filter(c => c.type === 'client');
    const connectedCompanies = clientCompanies.filter(c => c.xeroIntegration?.connected);
    const now = new Date();
    
    return {
      totalCompanies: clientCompanies.length,
      connectedCompanies: connectedCompanies.length,
      activeConnections: connectedCompanies.filter(c => {
        if (!c.xeroIntegration?.expiresAt) return false;
        return new Date(c.xeroIntegration.expiresAt) > now;
      }).length,
      expiredConnections: connectedCompanies.filter(c => {
        if (!c.xeroIntegration?.expiresAt) return false;
        return new Date(c.xeroIntegration.expiresAt) <= now;
      }).length,
      autoQuotesEnabled: connectedCompanies.filter(c => c.xeroIntegration?.autoCreateQuotes).length,
      autoInvoicesEnabled: connectedCompanies.filter(c => c.xeroIntegration?.autoSendInvoices).length,
    };
  }

  // ============================================================================
};
